import { ChangeEvent, KeyboardEvent, MouseEvent, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { FLOATING_HELPER_TEXT_SX } from "../SaleDepositReport/FilterField";
import {
  CUSTOM_THRESHOLD,
  CUSTOM_THRESHOLD_LABEL,
  MIN_DAYS,
  THRESHOLD_PRESETS,
  customDaysHint,
  parseCustomDays,
} from "./inactiveStores";

type ThresholdChoice = number | typeof CUSTOM_THRESHOLD;

const APPLY_KEY = "Enter";
const CUSTOM_INPUT_WIDTH = 96;
const CUSTOM_INPUT_SX = { width: CUSTOM_INPUT_WIDTH, ...FLOATING_HELPER_TEXT_SX } as const;

interface ThresholdSelectorProps {
  /** The threshold in force: the server's default until one is picked. */
  days: number;
  /** The server's bill lookback — the longest threshold that can still match. */
  maxDays: number;
  onChange: (days: number) => void;
}

/**
 * The "no bill for at least N days" cut-off: finance's four presets, or any whole number of
 * days inside the server's lookback window, applied with Enter or ตกลง.
 */
export default function ThresholdSelector({
  days,
  maxDays,
  onChange,
}: Readonly<ThresholdSelectorProps>) {
  const isPresetDays = THRESHOLD_PRESETS.some((preset) => preset.days === days);
  const [isCustomOpen, setIsCustomOpen] = useState(!isPresetDays);
  const [customText, setCustomText] = useState(isPresetDays ? "" : String(days));
  const [isCustomInvalid, setIsCustomInvalid] = useState(false);

  const selectedChoice: ThresholdChoice = isCustomOpen ? CUSTOM_THRESHOLD : days;

  const handleChoiceChange = (_event: MouseEvent<HTMLElement>, choice: ThresholdChoice | null) => {
    // An exclusive group reports null when the selected button is clicked again.
    if (choice === null) return;
    if (choice === CUSTOM_THRESHOLD) {
      setIsCustomOpen(true);
      return;
    }
    setIsCustomOpen(false);
    setIsCustomInvalid(false);
    onChange(choice);
  };

  const applyCustomDays = () => {
    const customDays = parseCustomDays(customText, maxDays);
    setIsCustomInvalid(customDays === null);
    if (customDays !== null) onChange(customDays);
  };

  const handleCustomTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCustomText(event.target.value);
    setIsCustomInvalid(false);
  };

  const handleCustomKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === APPLY_KEY) applyCustomDays();
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
      <ToggleButtonGroup
        size="small"
        color="primary"
        exclusive
        value={selectedChoice}
        onChange={handleChoiceChange}
      >
        {THRESHOLD_PRESETS.map((preset) => (
          <ToggleButton key={preset.days} value={preset.days}>
            {preset.label}
          </ToggleButton>
        ))}
        <ToggleButton value={CUSTOM_THRESHOLD}>{CUSTOM_THRESHOLD_LABEL}</ToggleButton>
      </ToggleButtonGroup>
      {isCustomOpen && (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="number"
            placeholder="วัน"
            value={customText}
            onChange={handleCustomTextChange}
            onKeyDown={handleCustomKeyDown}
            error={isCustomInvalid}
            helperText={isCustomInvalid ? customDaysHint(maxDays) : undefined}
            sx={CUSTOM_INPUT_SX}
            slotProps={{ htmlInput: { min: MIN_DAYS, max: maxDays } }}
          />
          <Button variant="contained" onClick={applyCustomDays}>
            ตกลง
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
